import { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Divider,
  Drawer,
  Input,
  Spin,
  Tag,
  message,
} from "antd";
import { FileTextOutlined, TeamOutlined } from "@ant-design/icons";
import {
  createPodcast,
  getPodcastAvailableSources,
} from "@vojomo-core/shared";
import type {
  CreatePodcastPayload,
  Podcast,
  PodcastAvailableSources,
  PodcastDataSource,
} from "@vojomo-core/shared";

const { TextArea } = Input;

interface DrawerDefaults {
  podcastType?: Podcast["podcast_type"];
  podcastLength?: Podcast["podcast_length"];
  podcastTone?: Podcast["podcast_tone"];
  sources?: PodcastDataSource[];
}

interface CreatePodcastDrawerProps {
  open: boolean;
  onClose: () => void;
  onCreated: (podcast: Podcast) => void;
  defaults?: DrawerDefaults;
}

// ── Config option definitions ─────────────────────────────────────────────

const PODCAST_TYPES: { value: Podcast["podcast_type"]; label: string; description: string }[] = [
  { value: "deep_dive", label: "Deep Dive", description: "Thorough narrative exploration with takeaways" },
  { value: "summary", label: "Summary", description: "Concise highlights, key points only" },
  { value: "analysis", label: "Analysis", description: "Data-driven breakdown with patterns and conclusions" },
  { value: "interview", label: "Interview", description: "Q&A conversation format between host and expert" },
  { value: "briefing", label: "Briefing", description: "Executive brief — precise, formal, action-oriented" },
];

const PODCAST_LENGTHS: { value: Podcast["podcast_length"]; label: string; sub: string }[] = [
  { value: "brief", label: "Brief", sub: "~3 min" },
  { value: "standard", label: "Standard", sub: "~8 min" },
  { value: "deep", label: "Deep", sub: "~15 min" },
];

const PODCAST_TONES: { value: Podcast["podcast_tone"]; label: string }[] = [
  { value: "analytical", label: "Analytical" },
  { value: "collaborative", label: "Collaborative" },
  { value: "critical", label: "Critical" },
  { value: "inspirational", label: "Inspirational" },
  { value: "neutral", label: "Neutral" },
];

// ── Period label helpers ──────────────────────────────────────────────────

function formatPeriodKey(periodKey: string): string {
  if (!periodKey) return periodKey;
  if (periodKey.includes("-Q")) {
    const [year, q] = periodKey.split("-Q");
    return `Q${q} ${year}`;
  }
  if (periodKey.includes("-W")) {
    const [year, w] = periodKey.split("-W");
    return `Week ${w}, ${year}`;
  }
  return periodKey;
}

// ── Segment heading ───────────────────────────────────────────────────────

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <div className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">
    {children}
  </div>
);

// ── Pill selector ─────────────────────────────────────────────────────────

interface PillOption<T> {
  value: T;
  label: string;
  sub?: string;
}

function PillSelector<T extends string>({
  options,
  selected,
  onSelect,
}: {
  options: PillOption<T>[];
  selected: T;
  onSelect: (v: T) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onSelect(opt.value)}
          className={`
            px-3 py-1.5 rounded-full text-sm font-medium border transition-all
            ${
              selected === opt.value
                ? "bg-blue-600 text-white border-blue-600"
                : "bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:text-blue-600"
            }
          `}
        >
          {opt.label}
          {opt.sub && (
            <span className={`ml-1 text-xs ${selected === opt.value ? "text-blue-200" : "text-gray-400"}`}>
              {opt.sub}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

// ── Main drawer ───────────────────────────────────────────────────────────

export const CreatePodcastDrawer = ({
  open,
  onClose,
  onCreated,
  defaults,
}: CreatePodcastDrawerProps) => {
  const [sources, setSources] = useState<PodcastAvailableSources | null>(null);
  const [loadingSources, setLoadingSources] = useState(false);

  // Form state
  const [title, setTitle] = useState("");
  const [selectedSources, setSelectedSources] = useState<PodcastDataSource[]>([]);
  const [customText, setCustomText] = useState("");
  const [customTextEnabled, setCustomTextEnabled] = useState(false);
  const [podcastType, setPodcastType] = useState<Podcast["podcast_type"]>("deep_dive");
  const [podcastLength, setPodcastLength] = useState<Podcast["podcast_length"]>("standard");
  const [podcastTone, setPodcastTone] = useState<Podcast["podcast_tone"]>("analytical");
  const [customPrompt, setCustomPrompt] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      loadSources();
      if (defaults) {
        if (defaults.podcastType) setPodcastType(defaults.podcastType);
        if (defaults.podcastLength) setPodcastLength(defaults.podcastLength);
        if (defaults.podcastTone) setPodcastTone(defaults.podcastTone);
        if (defaults.sources) setSelectedSources(defaults.sources);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const loadSources = async () => {
    setLoadingSources(true);
    try {
      const data = await getPodcastAvailableSources();
      setSources(data);
    } catch {
      message.error("Failed to load available data sources");
    } finally {
      setLoadingSources(false);
    }
  };

  const toggleSource = (src: PodcastDataSource) => {
    const existing = selectedSources.findIndex(
      (s) =>
        s.type === src.type &&
        s.session_id === src.session_id &&
        s.period_key === src.period_key &&
        s.building_block === src.building_block &&
        s.document_id === src.document_id
    );
    if (existing >= 0) {
      setSelectedSources((prev) => prev.filter((_, i) => i !== existing));
    } else {
      setSelectedSources((prev) => [...prev, src]);
    }
  };

  const isSourceSelected = (src: PodcastDataSource) =>
    selectedSources.some(
      (s) =>
        s.type === src.type &&
        s.session_id === src.session_id &&
        s.period_key === src.period_key &&
        s.building_block === src.building_block &&
        s.document_id === src.document_id
    );

  const handleCreate = async () => {
    if (!title.trim()) {
      message.warning("Please enter a title for your podcast");
      return;
    }
    const finalSources: PodcastDataSource[] = [...selectedSources];
    if (customTextEnabled && customText.trim()) {
      finalSources.push({ type: "custom_text", custom_text: customText.trim() });
    }
    if (finalSources.length === 0) {
      message.warning("Please select at least one data source");
      return;
    }

    setSubmitting(true);
    try {
      const payload: CreatePodcastPayload = {
        title: title.trim(),
        data_sources: finalSources,
        podcast_type: podcastType,
        podcast_length: podcastLength,
        podcast_tone: podcastTone,
        custom_prompt: customPrompt.trim() || undefined,
      };
      const podcast = await createPodcast(payload);
      onCreated(podcast);
      handleClose();
      message.success("Podcast is being generated — we'll notify you when it's ready.");
    } catch {
      message.error("Failed to create podcast. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setTitle("");
    setSelectedSources([]);
    setCustomText("");
    setCustomTextEnabled(false);
    setPodcastType("deep_dive");
    setPodcastLength("standard");
    setPodcastTone("analytical");
    setCustomPrompt("");
    onClose();
  };

  const platformDocs = sources?.platform_documents ?? [];
  const soloDocs = platformDocs.filter((d) => !d.is_team);
  const teamDocs = platformDocs.filter((d) => d.is_team);

  const hasAnySources =
    (sources?.qbr_periods?.length ?? 0) > 0 ||
    (sources?.weekly_periods?.length ?? 0) > 0 ||
    (sources?.objective_building_blocks?.length ?? 0) > 0 ||
    sources?.has_roadmap_content ||
    platformDocs.length > 0;

  const hasProgressSources =
    (sources?.qbr_periods?.length ?? 0) > 0 ||
    (sources?.weekly_periods?.length ?? 0) > 0 ||
    (sources?.objective_building_blocks?.length ?? 0) > 0 ||
    sources?.has_roadmap_content;

  return (
    <Drawer
      title="Create Podcast Episode"
      placement="right"
      width={460}
      open={open}
      onClose={handleClose}
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="primary"
            onClick={handleCreate}
            loading={submitting}
            disabled={loadingSources}
          >
            Generate Podcast
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-5">
        {/* Title */}
        <div>
          <SectionLabel>Episode Title</SectionLabel>
          <Input
            placeholder="e.g. Q1 2026 Team Review"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        <Divider className="my-0" />

        {/* Data Sources */}
        <div>
          <SectionLabel>Data Sources</SectionLabel>
          {loadingSources ? (
            <div className="flex justify-center py-4">
              <Spin size="small" />
            </div>
          ) : !hasAnySources ? (
            <p className="text-sm text-gray-400">
              No data found. Write or paste custom content below to get started.
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {/* Platform Documents — Solo */}
              {soloDocs.length > 0 && (
                <div className="mb-1">
                  <div className="flex items-center gap-1.5 text-xs text-gray-500 font-medium mb-2">
                    <FileTextOutlined />
                    My Documents
                  </div>
                  {soloDocs.map((doc) => (
                    <Checkbox
                      key={`doc:${doc.id}`}
                      checked={isSourceSelected({ type: "platform_document", document_id: doc.id })}
                      onChange={() => toggleSource({ type: "platform_document", document_id: doc.id })}
                      className="block mb-1.5"
                    >
                      <span className="text-sm">{doc.title}</span>
                    </Checkbox>
                  ))}
                </div>
              )}

              {/* Platform Documents — Team */}
              {teamDocs.length > 0 && (
                <div className="mb-1">
                  <div className="flex items-center gap-1.5 text-xs text-gray-500 font-medium mb-2">
                    <TeamOutlined />
                    Team Documents
                  </div>
                  {teamDocs.map((doc) => (
                    <Checkbox
                      key={`doc:${doc.id}`}
                      checked={isSourceSelected({ type: "platform_document", document_id: doc.id })}
                      onChange={() => toggleSource({ type: "platform_document", document_id: doc.id })}
                      className="block mb-1.5"
                    >
                      <span className="text-sm">{doc.title}</span>
                      <Tag color="blue" className="ml-2 text-xs">Team</Tag>
                    </Checkbox>
                  ))}
                </div>
              )}

              {/* Divider between docs and progress data */}
              {platformDocs.length > 0 && hasProgressSources && (
                <Divider className="my-1" />
              )}

              {/* QBR periods */}
              {sources?.qbr_periods?.map((p) => (
                <Checkbox
                  key={`qbr:${p.session_id}:${p.period_key}`}
                  checked={isSourceSelected({ type: "qbr_scoring", session_id: p.session_id, period_key: p.period_key })}
                  onChange={() => toggleSource({ type: "qbr_scoring", session_id: p.session_id, period_key: p.period_key })}
                >
                  <span className="text-sm">
                    <span className="font-medium">QBR Scoring</span>
                    <span className="text-gray-400 ml-1">— {formatPeriodKey(p.period_key)}</span>
                  </span>
                </Checkbox>
              ))}

              {/* Weekly periods */}
              {sources?.weekly_periods?.map((p) => (
                <Checkbox
                  key={`weekly:${p.session_id}:${p.period_key}`}
                  checked={isSourceSelected({ type: "weekly_progress", session_id: p.session_id, period_key: p.period_key })}
                  onChange={() => toggleSource({ type: "weekly_progress", session_id: p.session_id, period_key: p.period_key })}
                >
                  <span className="text-sm">
                    <span className="font-medium">Weekly Progress</span>
                    <span className="text-gray-400 ml-1">— {formatPeriodKey(p.period_key)}</span>
                  </span>
                </Checkbox>
              ))}

              {/* Objectives */}
              {sources?.objective_building_blocks?.map((block) => (
                <Checkbox
                  key={`obj:${block}`}
                  checked={isSourceSelected({ type: "objectives", building_block: block })}
                  onChange={() => toggleSource({ type: "objectives", building_block: block })}
                >
                  <span className="text-sm">
                    <span className="font-medium">Objectives</span>
                    <span className="text-gray-400 ml-1">— {block}</span>
                  </span>
                </Checkbox>
              ))}

              {/* Roadmap */}
              {sources?.has_roadmap_content && (
                <Checkbox
                  checked={isSourceSelected({ type: "roadmap_building_blocks" })}
                  onChange={() => toggleSource({ type: "roadmap_building_blocks" })}
                >
                  <span className="text-sm font-medium">Roadmap Building Blocks</span>
                </Checkbox>
              )}
            </div>
          )}

          {/* Custom text */}
          <div className="mt-3">
            <Checkbox
              checked={customTextEnabled}
              onChange={(e) => setCustomTextEnabled(e.target.checked)}
            >
              <span className="text-sm font-medium">Write or paste custom content</span>
            </Checkbox>
            {customTextEnabled && (
              <TextArea
                className="mt-2"
                rows={3}
                placeholder="Paste any text, notes, or context you want included…"
                value={customText}
                onChange={(e) => setCustomText(e.target.value)}
              />
            )}
          </div>
        </div>

        <Divider className="my-0" />

        {/* Format */}
        <div>
          <SectionLabel>Format</SectionLabel>
          <div className="flex flex-col gap-2">
            {PODCAST_TYPES.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setPodcastType(opt.value)}
                className={`
                  text-left px-3 py-2 rounded-lg border transition-all
                  ${
                    podcastType === opt.value
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-200 hover:border-gray-300"
                  }
                `}
              >
                <div className="text-sm font-medium text-gray-900">{opt.label}</div>
                <div className="text-xs text-gray-400">{opt.description}</div>
              </button>
            ))}
          </div>
        </div>

        <Divider className="my-0" />

        {/* Length */}
        <div>
          <SectionLabel>Length</SectionLabel>
          <PillSelector
            options={PODCAST_LENGTHS}
            selected={podcastLength}
            onSelect={setPodcastLength}
          />
        </div>

        <Divider className="my-0" />

        {/* Tone */}
        <div>
          <SectionLabel>Tone</SectionLabel>
          <PillSelector
            options={PODCAST_TONES}
            selected={podcastTone}
            onSelect={setPodcastTone}
          />
        </div>

        <Divider className="my-0" />

        {/* Focus prompt */}
        <div>
          <SectionLabel>Focus <span className="normal-case font-normal text-gray-400">(optional)</span></SectionLabel>
          <TextArea
            rows={3}
            placeholder="What should Vo focus on in this episode? e.g. 'Highlight alignment gaps between leaders'"
            value={customPrompt}
            onChange={(e) => setCustomPrompt(e.target.value)}
          />
        </div>
      </div>
    </Drawer>
  );
};
